<template>
  <div>
    <!-- enableQuickQuery 模式：带下拉候选的快速查询输入框 -->
    <template v-if="enableQuickQuery">
      <div class="sku-quick-input__wrapper">
        <a-select
          show-search
          allow-clear
          :value="keywords || undefined"
          :placeholder="placeholder || $t('扫码/输入条码，或点击右侧选择商品')"
          class="sku-quick-input"
          style="flex: 1"
          :default-active-first-option="false"
          :show-arrow="false"
          :filter-option="false"
          :not-found-content="quickQueryLoading ? undefined : null"
          @search="handleQuickSearch"
          @change="handleQuickSelect"
        >
          <a-spin v-if="quickQueryLoading" slot="notFoundContent" size="small" />
          <a-select-option v-for="item in quickSkuList" :key="item.skuCode" :option="item">
            {{ item.skuName + ' | ' + item.skuCode }}
          </a-select-option>
        </a-select>
        <a-icon type="appstore" class="sku-quick-input__suffix" @click.stop="openModal" />
      </div>
    </template>

    <!-- 默认模式 -->
    <template v-else>
      <a-input
        v-if="isRent"
        allow-clear
        :placeholder="$t('扫码/输入条码，或点击右侧选择商品')"
        v-model="keywords"
        class="sku-quick-input"
        @change="handleKeywordsChange"
        @pressEnter="handleKeywordsChange"
      >
        <a-icon
          slot="suffix"
          type="appstore"
          class="sku-quick-input__suffix"
          @click.stop="openModal"
        />
      </a-input>
      <a-input
        v-else
        allow-clear
        :placeholder="placeholder"
        v-model="keywords"
        class="sku-quick-input"
        @change="handleKeywordsChange"
      />
    </template>
    <a-modal
      :visible="visible"
      :title="title || $t('SKU快捷查询')"
      width="900px"
      :dialog-style="{ top: '20px' }"
      @cancel="handleCancel"
      :footer="null"
      :maskClosable="false"
    >
      <a-form-model :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
        <a-row v-if="previewSku || committedSku" style="margin-bottom: 20px">
          <a-col :span="24">
            <div class="recent-wrapper" style="display: flex; align-items: center; flex-wrap: wrap">
              <div class="recent-label">{{ $t('已选商品') }}：</div>
              <div class="recent-tags">
                <a-tag>
                  {{ formatSkuTagText(previewSku || committedSku) }}
                </a-tag>
              </div>
            </div>
          </a-col>
        </a-row>
        <a-row v-if="recentPairs.length" style="margin-bottom: 20px">
          <a-col :span="24">
            <div class="recent-wrapper" style="display: flex; align-items: center; flex-wrap: wrap">
              <div class="recent-label">{{ $t('历史选择') }}：</div>
              <div class="recent-tags">
                <a-tag
                  v-for="item in recentPairs"
                  :key="item.key"
                  closable
                  @close="(e) => handleRemoveRecent(e, item.key)"
                  @click="() => applyRecent(item)"
                >
                  {{ item.brandName }} / {{ item.modelName }}
                </a-tag>
              </div>
            </div>
          </a-col>
        </a-row>

        <a-row :gutter="16" style="margin-top: 20px">
          <a-col :span="12">
            <a-form-model-item>
              <template slot="label">
                {{ $t('品牌') }}
                <a-icon
                  type="sync"
                  class="text-link"
                  style="margin-left: 4px; padding-right: 4px"
                  :spin="brandLoading"
                  @click="handleRefreshBrandList"
                />
              </template>

              <a-spin :spinning="brandLoading" size="small">
                <div class="tag-group tag-group-scroll">
                  <span v-if="!brandOptions.length" style="color: rgba(0, 0, 0, 0.45)">
                    {{ $t('暂无品牌数据') }}
                  </span>
                  <a-checkable-tag
                    class="cursor-pointer"
                    v-for="item in brandOptions"
                    :key="item.value"
                    :checked="form.brandCode === item.value"
                    :disabled="brandLoading"
                    @change="(checked) => handleBrandTagChange(checked, item.value)"
                  >
                    {{ item.label }}
                  </a-checkable-tag>
                </div>
              </a-spin>
            </a-form-model-item>
          </a-col>

          <a-col :span="12">
            <a-form-model-item :label="$t('型号')">
              <a-select
                style="width: 100%"
                v-model="form.modelCode"
                :placeholder="$t('请先选品牌，再选择型号')"
                allow-clear
                show-search
                :disabled="!form.brandCode || skuRelationLoading"
                :filterOption="
                  (input, option) =>
                    (option.componentOptions?.children?.[0]?.text || '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                "
                @change="handleModelChange"
              >
                <a-select-option v-for="item in modelOptions" :key="item.value" :value="item.value">
                  {{ item.label }}
                </a-select-option>
              </a-select>
            </a-form-model-item>
          </a-col>
        </a-row>

        <a-spin
          :spinning="skuRelationLoading"
          :tip="$t('加载中')"
          :class="['sku-relation-spin', { 'sku-relation-spin--loading': skuRelationLoading }]"
        >
          <a-row
            v-if="specFields.length"
            :gutter="16"
            style="margin-top: 20px; display: flex; flex-wrap: wrap; gap: 20px 0"
          >
            <a-col v-for="f in specFields" :key="f.styleType" :span="12">
              <a-form-model-item :label="f.styleType">
                <div class="tag-group">
                  <a-checkable-tag
                    v-for="v in f.styleValues"
                    class="cursor-pointer"
                    :key="`${f.styleType}:${v}`"
                    :checked="selectedSpecs?.[f.styleType] === v"
                    :disabled="skuRelationLoading"
                    @change="(checked) => handleSpecTagChange(checked, f.styleType, v)"
                  >
                    {{ v }}
                  </a-checkable-tag>
                </div>
              </a-form-model-item>
            </a-col>
          </a-row>

          <div v-if="matchHint" style="margin-top: 10px; color: rgba(0, 0, 0, 0.65)">
            {{ matchHint }}
          </div>
        </a-spin>

        <a-row style="margin-top: 20px">
          <a-col :span="24" style="text-align: right">
            <a-button @click="handleCancel">{{ $t('取消') }}</a-button>
            <a-button
              type="primary"
              style="margin-left: 8px"
              :disabled="!canSave"
              @click="handleSave"
            >
              {{ $t('保存') }}
            </a-button>
          </a-col>
        </a-row>
      </a-form-model>
    </a-modal>
  </div>
</template>

<script>
import http from '@/service/axios'
import api from '@/service/api'
import { mapGetters } from 'vuex'
import debounce from '@common/debounce'

// 仅作为“优先展示/排序”的规格集合；最终必选项以接口返回 specOptions 为准
const PREFERRED_STYLE_TYPES = ['颜色', '运行内存', '机身存储', '成新', '版本']
const RECENT_CACHE_KEY = 'scm_sku_quick_query_recent_pairs_v1'
const BRAND_CACHE_KEY = 'scm_sku_quick_query_brand_options_v1'
const RECENT_MAX = 12

export default {
  name: 'SkuQuickQueryModal',
  props: {
    title: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    // 为 true 时，输入框支持直接按 skuCode 快速查询，找到匹配结果后自动 emit @selected
    enableQuickQuery: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      visible: false,
      skuRelationLoading: false,
      brandLoading: false,
      brandOptions: [],
      modelOptions: [],
      form: {
        brandCode: undefined,
        modelCode: undefined,
      },
      selectedSpecs: {},
      skuResult: null,
      matchHint: '',
      recentPairs: [],
      committedSku: null, // 已保存到父元素的单个选择
      keywords: '',
      // enableQuickQuery 模式相关
      quickQueryLoading: false,
      quickSkuList: [],
    }
  },
  computed: {
    ...mapGetters(['isRent']),
    selectedBrandRaw() {
      return this.brandOptions.find((b) => b.value === this.form.brandCode)?.raw || null
    },
    selectedModelRaw() {
      return this.modelOptions.find((m) => m.value === this.form.modelCode)?.raw || null
    },
    specFields() {
      const specOptions = this.skuResult?.specOptions || []
      const filtered = specOptions
        .filter((o) => PREFERRED_STYLE_TYPES.includes(o.styleType))
        .map((o) => ({
          styleType: o.styleType,
          styleValues: Array.isArray(o.styleValues) ? o.styleValues : [],
        }))
      // 固定顺序
      return PREFERRED_STYLE_TYPES.map((t) => filtered.find((x) => x.styleType === t)).filter(
        Boolean
      )
    },
    requiredStyleTypes() {
      // 以接口 specOptions 实际返回为准（避免某些型号没有“运行内存”等字段）
      return (this.specFields || []).map((x) => x.styleType)
    },
    isAllRequiredSelected() {
      if (!this.skuResult) return false
      return (this.requiredStyleTypes || []).every((t) => !!this.selectedSpecs?.[t])
    },
    matchedSkuRelations() {
      return this.findMatchedSkuRelations()
    },
    canSave() {
      // 仅当规格完整且能唯一匹配到一个 SKU 时，允许保存
      return this.isAllRequiredSelected && this.matchedSkuRelations.length === 1
    },
    previewSku() {
      if (!this.canSave) return null
      const matched = this.matchedSkuRelations?.[0]
      if (!matched) return null
      return this.buildSkuPayload(matched)
    },
  },
  methods: {
    formatSkuTagText(payload) {
      if (!payload) return ''
      const goodsName = payload.goodsName || payload.modelName || payload.brandName || ''
      return goodsName ? `${goodsName} / ${payload.skuCode}` : payload.skuCode
    },
    openModal() {
      this.open()
    },
    open({ brandCode, modelCode } = {}) {
      this.resetAll()
      this.visible = true
      this.loadRecentPairs()
      this.fetchBrandList().then(() => {
        if (brandCode) {
          this.form.brandCode = brandCode
          this.handleBrandChange(brandCode)
        }
        if (modelCode) {
          this.form.modelCode = modelCode
          this.handleModelChange(modelCode)
        }
      })
    },
    handleCancel() {
      this.visible = false
      this.$emit('cancel')
    },
    resetAll() {
      this.skuRelationLoading = false
      this.brandLoading = false
      this.brandOptions = []
      this.modelOptions = []
      this.form = { brandCode: undefined, modelCode: undefined }
      this.selectedSpecs = {}
      this.skuResult = null
      this.matchHint = ''
      // recentPairs 不在这里清空，避免 modal 内操作时闪烁
    },
    resetSpecs() {
      this.selectedSpecs = {}
      this.skuResult = null
      this.matchHint = ''
    },
    fetchBrandList() {
      return this.fetchBrandListWithCache()
    },
    parseBrandListToOptions(brandList) {
      return (brandList || []).map((b) => ({
        label: b.brandName,
        value: b.brandId,
        raw: b,
      }))
    },
    getBrandListFromCache() {
      try {
        const raw = localStorage.getItem(BRAND_CACHE_KEY)
        const brandList = raw ? JSON.parse(raw) : []
        return Array.isArray(brandList) ? brandList : []
      } catch (e) {
        return []
      }
    },
    setBrandListCache(brandList) {
      try {
        localStorage.setItem(BRAND_CACHE_KEY, JSON.stringify(brandList || []))
      } catch (e) {
        // ignore
      }
    },
    fetchBrandListWithCache({ forceRefresh = false } = {}) {
      if (!forceRefresh) {
        const cache = this.getBrandListFromCache()
        if (cache.length) {
          this.brandOptions = this.parseBrandListToOptions(cache)
          return Promise.resolve()
        }
      }
      this.brandLoading = true
      return new Promise((resolve) => {
        http({
          type: 'post',
          url: api.getUPDBrandList,
          data: {},
          hasLoading: false,
          success: (res) => {
            const brandList = res?.result?.brandList || []
            this.brandOptions = this.parseBrandListToOptions(brandList)
            this.setBrandListCache(brandList)
            this.brandLoading = false
            resolve()
          },
          fail: () => {
            this.brandLoading = false
            resolve()
          },
        })
      })
    },
    handleRefreshBrandList() {
      this.fetchBrandListWithCache({ forceRefresh: true })
    },
    fetchModelListByBrandCode(brandCode) {
      const brandRaw = this.brandOptions.find((b) => b.value === brandCode)?.raw
      const models = brandRaw?.brandModelList || []
      this.modelOptions = models.map((m) => ({
        label: m.modelName,
        value: m.modelCode,
        raw: m,
      }))
    },
    handleBrandChange(brandCode) {
      this.form.modelCode = undefined
      this.modelOptions = []
      this.resetSpecs()
      if (!brandCode) return
      this.fetchModelListByBrandCode(brandCode)
    },
    handleBrandTagChange(checked, brandCode) {
      // 单选：checked=false 代表取消选中
      const next = checked ? brandCode : undefined
      this.form.brandCode = next
      this.handleBrandChange(next)
    },
    handleModelChange(modelCode) {
      this.resetSpecs()
      if (!modelCode) return
      const modelName =
        this.modelOptions.find((m) => m.value === modelCode)?.raw?.modelName ||
        this.modelOptions.find((m) => m.value === modelCode)?.label
      if (!modelName) return
      this.querySkuRelationMap(modelCode, modelName)
    },
    querySkuRelationMap(modelCode, modelName) {
      this.skuRelationLoading = true
      http({
        type: 'post',
        url: api.querySkuRelationMap,
        data: { modelCode, modelName },
        hasLoading: false,
        success: (res) => {
          this.skuResult = res?.result || null
          this.skuRelationLoading = false
          this.autoPickSingles()
          // 不自动提交到父组件；仅根据规格匹配情况更新提示
        },
        fail: () => {
          this.skuRelationLoading = false
        },
      })
    },
    autoPickSingles() {
      const next = { ...(this.selectedSpecs || {}) }
      for (const f of this.specFields) {
        const values = f.styleValues || []
        if (values.length === 1) next[f.styleType] = values[0]
      }
      this.selectedSpecs = next
      this.updateMatchHint()
    },
    handleSpecTagChange(checked, styleType, value) {
      const next = { ...(this.selectedSpecs || {}) }
      next[styleType] = checked ? value : undefined
      this.selectedSpecs = next
      this.updateMatchHint()
      // 不自动添加：由用户点击“添加”按钮确认
    },
    updateMatchHint() {
      if (!this.skuResult) {
        this.matchHint = ''
        return
      }
      const matched = this.findMatchedSkuRelations()
      if (!this.isAllRequiredSelected) {
        this.matchHint = matched.length
          ? `${this.$t('当前可匹配SKU数')}: ${matched.length}`
          : this.$t('当前条件下无可匹配SKU')
        return
      }
      if (matched.length === 1) {
        this.matchHint = `${this.$t('已匹配到SKU')}: ${matched[0].skuCode}`
      } else if (matched.length === 0) {
        this.matchHint = this.$t('当前条件下无可匹配SKU')
      } else {
        this.matchHint = `${this.$t('匹配到多个SKU，请继续调整条件')} (${matched.length})`
      }
    },
    findMatchedSkuRelations() {
      const relations = this.skuResult?.skuRelations || []
      const selected = this.selectedSpecs || {}
      return relations.filter((r) => {
        const specs = r?.specs || []
        // 仅按已选择的字段过滤（支持未选齐时做 hint）
        return Object.keys(selected).every((styleType) => {
          const v = selected[styleType]
          if (!v) return true
          return specs.some((s) => s.styleType === styleType && s.styleValue === v)
        })
      })
    },
    buildSkuPayload(matchedRelation) {
      if (!matchedRelation || !this.skuResult) return null
      return {
        skuCode: matchedRelation.skuCode,
        goodsId: this.skuResult.goodsId,
        goodsCode: this.skuResult.goodsCode,
        goodsName: this.skuResult.goodsName,
        brandName: this.skuResult.brandName,
        modelName: this.skuResult.modelName,
        selectedSpecs: { ...(this.selectedSpecs || {}) },
        raw: this.skuResult,
      }
    },

    loadRecentPairs() {
      try {
        const raw = localStorage.getItem(RECENT_CACHE_KEY)
        const list = raw ? JSON.parse(raw) : []
        this.recentPairs = Array.isArray(list) ? list : []
      } catch (e) {
        this.recentPairs = []
      }
    },
    persistRecentPairs() {
      try {
        localStorage.setItem(RECENT_CACHE_KEY, JSON.stringify(this.recentPairs || []))
      } catch (e) {
        // ignore
      }
    },
    saveRecentPair({ brandCode, brandName, modelCode, modelName }) {
      if (!brandCode || !modelCode) return
      const key = `${brandCode}__${modelCode}`
      const now = Date.now()
      const next = [
        {
          key,
          brandCode,
          brandName: brandName || '',
          modelCode,
          modelName: modelName || '',
          ts: now,
        },
        ...(this.recentPairs || []).filter((x) => x && x.key !== key),
      ].slice(0, RECENT_MAX)
      this.recentPairs = next
      this.persistRecentPairs()
    },
    handleRemoveRecent(e, key) {
      // 防止 close 冒泡触发 click 选中
      try {
        e && e.preventDefault && e.preventDefault()
        e && e.stopPropagation && e.stopPropagation()
      } catch (err) {
        // ignore
      }
      this.recentPairs = (this.recentPairs || []).filter((x) => x && x.key !== key)
      this.persistRecentPairs()
    },
    applyRecent(item) {
      if (!item?.brandCode || !item?.modelCode) return
      this.fetchBrandList().then(() => {
        this.form.brandCode = item.brandCode
        this.handleBrandChange(item.brandCode)
        this.$nextTick(() => {
          this.form.modelCode = item.modelCode
          this.handleModelChange(item.modelCode)
        })
      })
    },

    handleSave() {
      this.visible = false
      if (!this.canSave) return

      const matched = this.matchedSkuRelations?.[0]
      const payload = this.buildSkuPayload(matched)
      if (!payload?.skuCode) return

      this.saveRecentPair({
        brandCode: this.form.brandCode,
        brandName: this.selectedBrandRaw?.brandName || this.skuResult.brandName,
        modelCode: this.form.modelCode,
        modelName: this.selectedModelRaw?.modelName || this.skuResult.modelName,
      })

      this.committedSku = payload
      this.keywords = payload.skuCode
      // emit keywords 给父元素
      this.$emit('save', payload.skuCode)
      // 兼容旧事件名（如果父组件仍监听 selected）
      this.$emit('selected', payload)
    },

    handleKeywordsChange: debounce(function () {
      this.$emit('change', this.keywords)
    }, 300),

    // ---- enableQuickQuery 相关 ----
    handleQuickSearch: debounce(function (value) {
      // console.log('value', value)
      this.quickSkuList = []
      if (!value) return
      this.quickQueryLoading = true
      http({
        url: api.getSkuList,
        type: 'post',
        data: { keyWord: value },
        hasLoading: false,
        success: (res) => {
          const list = res?.result?.skuResVO || []
          this.quickSkuList = list
          this.quickQueryLoading = false
          // 精确匹配：skuCode 完全一致时自动选中
          const exact = list.find(
            (item) => item.skuCode && item.skuCode.toLowerCase() === value.toLowerCase()
          )
          if (exact) {
            this.handleQuickSelect(exact.skuCode, null, exact)
          }
        },
        fail: () => {
          this.quickQueryLoading = false
        },
      })
    }, 500),

    handleQuickSelect(skuCode, _option, rawItem) {
      // console.log(skuCode, _option, rawItem)
      // ant-design-vue v1 的 a-select @change 第二个参数是 VNode option
      // rawItem 是精确匹配时直接传入的原始对象
      let item = rawItem
      if (!item) {
        // 从下拉选项中找
        item = this.quickSkuList.find((s) => s.skuCode === skuCode)
      }
      if (!item || !item.skuCode) {
        this.keywords = undefined
        return
      }

      this.keywords = item.skuCode
      this.committedSku = {
        skuCode: item.skuCode,
        goodsName: item.skuName || '',
        modelName: item.skuName || '',
        brandName: '',
        goodsId: item.goodsId || '',
        goodsCode: item.goodsCode || '',
        selectedSpecs: this._parseSkuStyleList(item.skuStyleList),
        raw: item,
        // 兼容 addGoodsToTable 需要的字段
        skuName: item.skuName || item.skuCode,
        barcode: item.skuCode,
        skuStyleList: item.skuStyleList || [],
      }
      this.$emit('selected', this.committedSku)
      this.$emit('save', item.skuCode)
    },

    _parseSkuStyleList(skuStyleList) {
      if (!Array.isArray(skuStyleList)) return {}
      return skuStyleList.reduce((acc, s) => {
        if (s && s.styleType) acc[s.styleType] = s.styleValue
        return acc
      }, {})
    },
  },
}
</script>

<style scoped>
.sku-quick-input {
  cursor: text;
}
.sku-quick-input__wrapper {
  display: flex !important;
  align-items: center;
  position: relative;
  width: 100%;
}
.sku-quick-input__wrapper .sku-quick-input {
  width: 100%;
}
.sku-quick-input__wrapper .sku-quick-input__suffix {
  margin-left: 8px;
  flex-shrink: 0;
}
.sku-quick-input__suffix {
  cursor: pointer;
  color: rgba(0, 0, 0, 0.45);
}
.sku-quick-input__suffix:hover {
  color: rgba(0, 0, 0, 0.85);
}
.recent-wrapper {
  display: flex;
  align-items: flex-start;
}
.recent-label {
  width: 84px;
  line-height: 32px;
  color: rgba(0, 0, 0, 0.65);
}
.recent-tags {
  flex: 1;
}
.recent-tags :deep(.ant-tag) {
  cursor: pointer;
  user-select: none;
}

.tag-group {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-top: 4px;
}
.tag-group-scroll {
  max-height: 132px;
  overflow: auto;
  padding-right: 4px;
}

.cursor-pointer {
  cursor: pointer;
  user-select: none;
}

/* 请求 relationMap 时内容可能尚未渲染，给 Spin 一个最小高度避免指示器贴顶不明显 */
.sku-relation-spin--loading {
  min-height: 120px;
}
</style>
